import { PlusFolderIcon } from '@mm/icons';
import { useSafeTimeout } from '@mm/hooks';
import classnames from 'classnames';
import { cloneDeep, get } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Draggable,
  DraggableStateSnapshot,
  DragStart,
  DropResult,
} from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { CardHeader } from 'reactstrap';
import { MMdndDroppable } from '../../../../../common/components/MMdndDroppable/MMdndDroppable';
import { SearchInput } from '../../../../../common/components/SearchInput/SearchInput';
import {
  DRGBL_PAGE,
  DRGBL_PAGE_GROUP,
} from '../../../../../common/constants/draggables';
import {
  ensureSequentialOrder,
  getIdFromDNDId,
  hasDuplicateSortOrders,
  isOrderBroken,
  reorder,
  updateSortOrdersToArrayOrder,
} from '../../../../../common/helpers/utils/sorting';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { usePageGroupCRUDActions } from '@mm/state/actions/PageGroupCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import PageModel, { PageKind } from '@mm/state/model/PageModel';
import { selectPageEntitiesByPageGroupsDictionary } from '@mm/state/selectors/pageSelectors';
import {
  activeChatbotEditorPageSelector,
  activeChatbotIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Line } from '@mm/components';
import { colors } from '../../../beta/styles/colors';
import PageEntity from '../../requestModels/PageEntity';
import PageGroupEntity from '../../requestModels/PageGroupEntity';
import { MainMenu } from '../MainMenu/MainMenu';
import { PageGroup } from '../PageGroup/PageGroup';
import PageGroupEditor from '../PageGroupEditor/PageGroupEditor';
import './PageOverviewPane.scss';

export const DEFAULT_GROUP_NAME = 'Default Folder';
const NOT_IN_FOLDER_GROUP_NAME = 'Not in Folder';
export const OTN_GROUP_NAME = 'OTN Widget';
export const SMS_GROUP_NAME = 'SMS Opt-in Widget';

type ExtendedPageGroup = PageGroupModel & {
  sortedPages: PageModel[];
  show: boolean;
};
type Props = {
  isMobile: boolean;
  handleDialoguesPaneState: (isDialoguePaneOpen: boolean) => void;
};

export const PageOverviewPane = ({
  handleDialoguesPaneState,
  isMobile,
}: Props) => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const page = useSelector(activeChatbotEditorPageSelector);
  const pages = useSelector((state: StoreState) => state.page.entities);
  const pagesByPageGroupsHash = useSelector(
    selectPageEntitiesByPageGroupsDictionary,
  );

  const viewActions = useViewActions();
  const pageGroupCRUDActions = usePageGroupCRUDActions();
  const pageCRUDActions = usePageCRUDActions();

  const [groupModal, setGroupModal] = useState(false);
  const [editedGroupId, setEditedGroupId] = useState<number>();
  const [draggedPageId, setDraggedPageId] = useState<number | null>();
  const [searchString, setSearchString] = useState('');
  const [pageGroupsFiltered, setPageGroupsFiltered] = useState<
    ExtendedPageGroup[]
  >([]);
  const [defaultPageGroupsFiltered, setDefaultPageGroupsFiltered] = useState<
    ExtendedPageGroup[]
  >([]);
  const mainMenuSelected = useSelector(
    (state: StoreState) => state.view.chatbotEditor.mainMenuSelected,
  );
  const [needToFilterPages, setNeedToFilterPages] = useState(false);

  const searchPageInputRef = useRef<HTMLInputElement>();

  const { safeTimeout } = useSafeTimeout();

  const getSortedPageGroupPages = (pageGroupId: number | null) => {
    const compareValue = searchString.toLowerCase();

    const pageGroupWithoutFilter = pageGroupId
      ? pagesByPageGroupsHash[pageGroupId] || []
      : ([] as PageModel[]);

    let pageGroupPages = pageGroupWithoutFilter.filter(page => {
      let isNeededName = true;
      if (needToFilterPages) {
        isNeededName = page.name.toLowerCase().indexOf(compareValue) !== -1;
      }
      return isNeededName;
    });

    if (pageGroupPages.length > 0) {
      pageGroupPages = pageGroupPages.filter(page =>
        [
          null,
          PageKind.OnboardingWelcomePage,
          PageKind.OTNPage,
          PageKind.SMSReplyPage,
          PageKind.StartBot,
          PageKind.AwayResponder,
          PageKind.WelcomePage,
        ].some(kind => kind === page.kind),
      );

      pageGroupPages = hasDuplicateSortOrders(pageGroupPages)
        ? // First option sorts similar to how it was sorted before introducinng sortOrder
          pageGroupPages.sort((p1, p2) => p1.id - p2.id)
        : pageGroupPages.sort((p1, p2) => p1.sortOrder - p2.sortOrder);
    }
    return pageGroupPages;
  };

  const generatePageStructure = () => {
    const pageGroupWithPages = pageGroups
      .map(group => {
        const sortedPages = getSortedPageGroupPages(group.id);
        return {
          ...group,
          sortedPages,
          show: false,
        };
      })
      .sort((pg1, pg2) =>
        // Sort by page group sort order, with tie breaker given to page group name
        pg1.sortOrder === pg2.sortOrder
          ? pg1.name.localeCompare(pg2.name)
          : pg1.sortOrder - pg2.sortOrder,
      );
    setDefaultPageGroupsFiltered(pageGroupWithPages);
    setPageGroupsFiltered(pageGroupWithPages);
  };

  useEffect(() => {
    if (pages.length > 0 && pageGroups.length > 0) {
      generatePageStructure();
    }
    pageCRUDActions.retrieveAll(activeChatbotId);
    pageGroupCRUDActions.retrieveAll(activeChatbotId);
  }, []);

  useEffect(() => {
    generatePageStructure();
  }, [pageGroups, pages]);

  const onCreatePageGroup = (pageGroup: PageGroupModel) => {
    const { name } = pageGroup;
    const maxSortOrder = Math.max(
      ...pageGroups.map(pageGroup => pageGroup.sortOrder),
      0,
    );
    const entity = PageGroupEntity.make(
      activeChatbotId,
      name,
      maxSortOrder + 1,
    );
    pageGroupCRUDActions.create(entity);
  };

  const defaultPage = useMemo(
    () => pages.filter(p => p.kind === PageKind.DefaultPage),
    [pages],
  )?.[0];

  const getRegularPagesByGroupId = (pageGroupId: number) =>
    pages.filter(p => p.kind === null && p.pageGroupId === pageGroupId);

  const fixPagesSortOrder = (pages: PageModel[]) => {
    const correctOrder = ensureSequentialOrder(pages) as PageModel[];
    const fixedPagesPatch = correctOrder.map((p, i) => ({
      id: p.id,
      sortOrder: i,
    }));
    pageCRUDActions.batchUpdate(fixedPagesPatch);
    return fixedPagesPatch.length;
  };

  const getNewPageSortOrder = (pageGroupId: number) => {
    const pages = getRegularPagesByGroupId(pageGroupId);

    if (isOrderBroken(pages)) {
      return fixPagesSortOrder(pages);
    }
    return pages.length;
  };

  const onCreatePage = async (page: PageModel) => {
    const { pageGroupId, name, notes } = page;
    const sortOrder = getNewPageSortOrder(pageGroupId);
    const entity = PageEntity.make(
      activeChatbotId,
      pageGroupId,
      name,
      notes,
      sortOrder,
    );
    const result = await pageCRUDActions.create(entity);
    if (result && result.page) {
      viewActions.activatePageEditor(result.page.id);
    }
    setDialguesPaneState();
  };

  const onClonePage = (pageId: number) =>
    pageCRUDActions.clone({ chatbotId: activeChatbotId, pageId });

  const onClickDeletePage = (pageId: number) => {
    const selectedPage = pages.find(p => p.id === pageId);

    if (selectedPage) {
      const groupPages = getRegularPagesByGroupId(selectedPage.pageGroupId);
      viewActions.showConfirmDeletePage(pageId, groupPages);
    }
  };

  const onSelectPage = (pageId: number) => {
    viewActions.unselectMainMenu();
    viewActions.activatePageEditor(pageId);
    setDialguesPaneState();
  };

  const setDialguesPaneState = () => {
    if (isMobile) {
      handleDialoguesPaneState(false);
    }
  };

  const openGroupModal = (groupId?: number) => {
    setGroupModal(true);
    setEditedGroupId(groupId);
  };

  const closeGroupModal = () => {
    setGroupModal(false);
    setEditedGroupId(undefined);
  };

  const reorderPages = ({ source, destination }: any) => {
    const current = getSortedPageGroupPages(getIdFromDNDId(source.droppableId));
    const next = getSortedPageGroupPages(
      getIdFromDNDId(destination.droppableId),
    );

    // moving in the same list
    if (source.droppableId === destination.droppableId) {
      const reordered = reorder(current, source.index, destination.index);
      return reordered;
    }
    // moving between two lists
    const target = cloneDeep(current[source.index]);
    target.pageGroupId = getIdFromDNDId(destination.droppableId) as number;
    current.splice(source.index, 1);
    next.splice(destination.index, 0, target);
    return [...current, ...next];
  };

  const getPagesUpdatePatch = (
    originalPages: PageModel[],
    reorderedPages: PageModel[],
  ) => {
    type PageOrderAndGroupHash = {
      [key: number]: { sortOrder: number; pageGroupId: number };
    };
    const originalPageOrderAndGroupHash: PageOrderAndGroupHash = {};

    originalPages.forEach(p => {
      originalPageOrderAndGroupHash[p.id] = {
        sortOrder: p.sortOrder,
        pageGroupId: p.pageGroupId,
      };
    });

    const updatePatch: Partial<PageModel>[] = [];
    const reorderedWithCorrectSortOrder: PageModel[] =
      updateSortOrdersToArrayOrder(reorderedPages);

    reorderedWithCorrectSortOrder.forEach(p => {
      if (originalPageOrderAndGroupHash[p.id]) {
        const pageUpdatePatch: Partial<PageModel> = { id: p.id };
        if (originalPageOrderAndGroupHash[p.id].sortOrder !== p.sortOrder) {
          pageUpdatePatch.sortOrder = p.sortOrder;
        }
        if (originalPageOrderAndGroupHash[p.id].pageGroupId !== p.pageGroupId) {
          pageUpdatePatch.pageGroupId = p.pageGroupId;
        }
        if (Object.keys(pageUpdatePatch).length > 1) {
          updatePatch.push(pageUpdatePatch);
        }
      }
    });
    return updatePatch;
  };

  const onSearchUpdate = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    const valueLowCase = value.toLowerCase();
    const needExpand = !!valueLowCase;

    if (!valueLowCase) {
      setSearchString(value);
      setPageGroupsFiltered(defaultPageGroupsFiltered);
      setNeedToFilterPages(true);
      return;
    }

    const updatedpageGroups: ExtendedPageGroup[] = defaultPageGroupsFiltered
      .filter(pageGroup => {
        const { sortedPages } = pageGroup;
        return sortedPages.some(
          page => page.name.toLowerCase().indexOf(valueLowCase) !== -1,
        );
      })
      .map((pageGroup: any) => {
        const sortedPages = get(pageGroup, 'sortedPages', []);
        return {
          ...pageGroup,
          show: needExpand,
          sortedPages: sortedPages.slice(),
        };
      });

    setSearchString(value);
    setPageGroupsFiltered(updatedpageGroups);
    setNeedToFilterPages(true);

    // This is an ugly hack to work around some focus issues within react-beautiful-dnd (MM-2090)
    safeTimeout(() => {
      if (searchPageInputRef.current) {
        searchPageInputRef.current.focus();
      }
    }, 100);
  };

  const onDragStart = (start: DragStart) =>
    setDraggedPageId(getIdFromDNDId(start.draggableId));

  const showGroup = (group: ExtendedPageGroup) => {
    if (group.name !== NOT_IN_FOLDER_GROUP_NAME) return true;

    return group.sortedPages.length !== 0;
  };

  const isNotEditableGroup = (pageGroupName: string) => {
    return (
      pageGroupName === DEFAULT_GROUP_NAME ||
      pageGroupName === NOT_IN_FOLDER_GROUP_NAME ||
      pageGroupName === OTN_GROUP_NAME ||
      pageGroupName === SMS_GROUP_NAME
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    if (!destination) {
      setDraggedPageId(undefined);
      return;
    }

    if (type === DRGBL_PAGE_GROUP) {
      const draggablePageGroups = pageGroupsFiltered.filter(
        pg => showGroup(pg) && !isNotEditableGroup(pg.name),
      );
      const itemToMove = draggablePageGroups[source.index];
      const itemToInsertAfter = draggablePageGroups[destination.index];
      const sourceIndex = pageGroupsFiltered.indexOf(itemToMove);
      const destIndex = pageGroupsFiltered.indexOf(itemToInsertAfter);
      const reorderedPageGroups = reorder(
        pageGroupsFiltered,
        sourceIndex,
        destIndex,
      );

      // Special page groups should always have sort order 0
      reorderedPageGroups
        .filter(pg => isNotEditableGroup(pg.name))
        .forEach(pg => {
          pg.sortOrder = 0;
        });
      // Regular page groups start sort order from 1
      reorderedPageGroups
        .filter(pg => !isNotEditableGroup(pg.name))
        .forEach((pg, index) => {
          pg.sortOrder = index + 1;
        });

      const reorderedPGMap = new Map(
        reorderedPageGroups.map(pg => [pg.id, pg]),
      );
      const diff: Pick<PageGroupModel, 'id' | 'sortOrder'>[] = [];
      for (let i = 0; i < pageGroupsFiltered.length; i += 1) {
        const orig = pageGroupsFiltered[i];
        const reordered = reorderedPGMap.get(orig.id);
        if (reordered && orig.sortOrder !== reordered.sortOrder) {
          diff.push({ id: reordered.id, sortOrder: reordered.sortOrder });
        }
      }

      if (diff.length === 0) return;
      pageGroupCRUDActions.batchUpdate(diff, activeChatbotId);
      return;
    }

    const reorderedPages = reorderPages(result);
    const updatePatch = getPagesUpdatePatch(pages, reorderedPages);
    if (updatePatch.length > 0) {
      pageCRUDActions.batchUpdate(updatePatch);
    }
    setDraggedPageId(undefined);
  };

  const renderGroupEditor = () => {
    const editedPageGroup = editedGroupId
      ? pageGroups.find(pageGroup => pageGroup.id === editedGroupId)
      : null;

    return (
      <PageGroupEditor
        closeModal={closeGroupModal}
        onUpdateGroup={pageGroupCRUDActions.update}
        onCreateGroup={onCreatePageGroup}
        showModal={groupModal}
        pageGroup={editedPageGroup}
      />
    );
  };

  const onDragUpdate = () => {};

  const renderPageGroupPages = (
    pageGroup: ExtendedPageGroup,
    index: number,
    snapshot?: DraggableStateSnapshot,
  ) => {
    const selectedPageGroupPresence = page?.pageGroupId;
    const selectedPageGroupId = selectedPageGroupPresence || 0;
    return (
      <MMdndDroppable
        droppableId={`drpbl-pageGroupId-${pageGroup.id}`}
        type={DRGBL_PAGE}
        key={pageGroup.id}
        onDragStart={onDragStart}
        onDragUpdate={onDragUpdate}
        onDragEnd={onDragEnd}
      >
        {provided => (
          <div ref={provided.innerRef} key={index} {...provided.droppableProps}>
            <PageGroup
              key={pageGroup.id}
              onCreatePage={onCreatePage}
              onClonePage={onClonePage}
              onDeletePage={onClickDeletePage}
              onDeletePageGroup={viewActions.showConfirmDeletePageGroup}
              onSelectPage={onSelectPage}
              entity={pageGroup}
              showed={selectedPageGroupId === pageGroup.id || pageGroup.show}
              defaultGroup={isNotEditableGroup(pageGroup.name)}
              showEditModal={openGroupModal}
              pageGroupPages={getSortedPageGroupPages(pageGroup.id)}
              draggedPageId={Number(draggedPageId)}
              isDragging={!!snapshot?.isDragging}
            />
            {provided.placeholder}
          </div>
        )}
      </MMdndDroppable>
    );
  };

  const displayedPageGroups = pageGroupsFiltered.filter(pg => showGroup(pg));
  const specialPageGroups = displayedPageGroups.filter(pg =>
    isNotEditableGroup(pg.name),
  );
  const draggablePageGroups = displayedPageGroups.filter(
    pg => !isNotEditableGroup(pg.name),
  );

  const defaultPageClassNames = classnames('page-overview__default-dialogue', {
    'page-overview__default-dialogue--selected':
      defaultPage?.id === page?.id && !mainMenuSelected,
  });

  const renderDragglePageGroups = () => {
    const groupsToRender = draggablePageGroups.map((pageGroup, index) => (
      <Draggable
        draggableId={`draggable-pageGroupId${pageGroup.id}`}
        index={index}
        type={DRGBL_PAGE_GROUP}
        key={pageGroup.id}
      >
        {(groupDraggableProvided, snapshot) => (
          <div
            ref={groupDraggableProvided.innerRef}
            key={index}
            {...groupDraggableProvided.draggableProps}
            {...groupDraggableProvided.dragHandleProps}
          >
            {renderPageGroupPages(pageGroup, index, snapshot)}
          </div>
        )}
      </Draggable>
    ));
    return groupsToRender;
  };

  return (
    <div className="page-overview">
      <div className="page-overview__outer-container">
        <div className="page-overview__card">
          <div className="page-overview__search-page-box">
            <SearchInput
              className="page-overview__dialogue-search"
              placeholder="Search Dialogues"
              onChange={onSearchUpdate}
              value={searchString}
            />
          </div>
        </div>
        <div className="page-overview__card">
          <div className="page-overview__top-links">
            {defaultPage && (
              <div
                className={defaultPageClassNames}
                onClick={() => onSelectPage(defaultPage.id)}
              >
                <div className="page-overview__default-dialogue--title">
                  {defaultPage.name}
                </div>
                <div className="page-overview__default-dialogue--notes">
                  Message is given when no other option is available
                </div>
              </div>
            )}
            <MainMenu
              setDialguesPaneState={setDialguesPaneState}
              isMobile={isMobile}
            />
          </div>
        </div>
        <Line className="page-overview__line-separator" />
        <div>
          <CardHeader className="page-overview__card-header--groups">
            <div className="page-overview__group-name page-overview__group-name--default">
              Folders
            </div>
            <div
              onClick={_e => openGroupModal()}
              className="page-overview__add-group-button"
            >
              <PlusFolderIcon
                className="page-overview__add-group-button--plus-folder"
                color={colors.accentBlue}
              />
            </div>
          </CardHeader>
        </div>
        <MMdndDroppable
          droppableId="drpbl-pageGroupMain"
          type={DRGBL_PAGE_GROUP}
          onDragStart={onDragStart}
          onDragUpdate={onDragUpdate}
          onDragEnd={onDragEnd}
        >
          {groupDroppableProvided => (
            <div
              ref={groupDroppableProvided.innerRef}
              {...groupDroppableProvided.droppableProps}
            >
              {specialPageGroups.map((pageGroup, index) =>
                renderPageGroupPages(pageGroup, index),
              )}
              {renderDragglePageGroups()}
              {groupDroppableProvided.placeholder}
            </div>
          )}
        </MMdndDroppable>
        {renderGroupEditor()}
      </div>
    </div>
  );
};
